import { Table } from "components/Table";
import { useMemo } from "react";

import type { HeatmapCellValue, HeatmapRange } from "./types";

import { buildHeatmapTableData, buildHeatmapTableHeaders } from "./buildHeatmapTableConfig";
import { formatHeatmapValue, getHeatmapInteractionCellClass } from "./heatmapScale";
import { HeatmapValueModal } from "./HeatmapValueModal";
import { useHeatmapRangeSelection } from "./useHeatmapRangeSelection";

export type HeatmapRangeEditorProps = {
  className?: string;
  columnLabels: string[];
  formatValue?: (value: HeatmapCellValue) => string;
  getInteractionCellClass?: () => string;
  labels: HeatmapRangeEditorLabels;
  onValuesChange: (values: HeatmapCellValue[][]) => void;
  range: HeatmapRange;
  readOnly?: boolean;
  rowHeaderLabel: string;
  rowHeight?: string;
  rowLabels: string[];
  values: HeatmapCellValue[][];
};

type HeatmapRangeEditorLabels = {
  applyLabel: string;
  cancelLabel: string;
  getModalDescription: (cellCount: number) => string;
  modalTitle: string;
  valueLabel: string;
};

const noopCellHandler = () => undefined;
const neverCellMatch = () => false;

export const HeatmapRangeEditor = ({
  className,
  columnLabels,
  formatValue = formatHeatmapValue,
  getInteractionCellClass = getHeatmapInteractionCellClass,
  labels,
  onValuesChange,
  range,
  readOnly = false,
  rowHeaderLabel,
  rowHeight = "2.5rem",
  rowLabels,
  values,
}: HeatmapRangeEditorProps) => {
  const {
    applyValue,
    handleCellClick,
    handleCellEnter,
    handleCellLeave,
    isCellHighlighted,
    isCellHovered,
    modalOpen,
    pendingCellCount,
    resetSelection,
  } = useHeatmapRangeSelection(values, onValuesChange);

  const data = useMemo(() => buildHeatmapTableData(values, rowLabels), [rowLabels, values]);
  const headers = useMemo(
    () =>
      buildHeatmapTableHeaders({
        columnLabels,
        formatValue,
        getInteractionCellClass,
        isCellHighlighted: readOnly ? neverCellMatch : isCellHighlighted,
        isCellHovered: readOnly ? neverCellMatch : isCellHovered,
        onCellClick: readOnly ? noopCellHandler : handleCellClick,
        onCellEnter: readOnly ? noopCellHandler : handleCellEnter,
        range,
        rowHeaderLabel,
      }),
    [
      columnLabels,
      formatValue,
      getInteractionCellClass,
      handleCellClick,
      handleCellEnter,
      isCellHighlighted,
      isCellHovered,
      range,
      readOnly,
      rowHeaderLabel,
    ]
  );

  return (
    <>
      <Table
        className={className}
        data={data}
        disableRowHover
        headers={headers}
        idColumnName="id"
        onCellLeave={readOnly ? undefined : handleCellLeave}
        rowHeight={rowHeight}
      />
      {!readOnly && (
        <HeatmapValueModal
          applyLabel={labels.applyLabel}
          cancelLabel={labels.cancelLabel}
          description={labels.getModalDescription(pendingCellCount)}
          onApply={applyValue}
          onClose={resetSelection}
          open={modalOpen}
          title={labels.modalTitle}
          valueLabel={labels.valueLabel}
        />
      )}
    </>
  );
};
